import { useNavigate } from "react-router-dom";
import request from "@/lib/api/handler";
import { useAuth0 } from "@auth0/auth0-react";
import { useMutation } from "@tanstack/react-query";
import { QueryKeys, useInvalidateQuery } from "@/queries/utils";

interface ClaimOpportunitiesResponse {
  claimed: string[];
  errors: { token: string; error: string }[];
}

export const useClaimOpportunities = () => {
  const invalidate = useInvalidateQuery();
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();

  const {
    mutate: claimOpportunities,
    isPending: claimOpportunitiesPending,
    error: claimOpportunitiesError,
  } = useMutation({
    mutationFn: async () => {
      const token = await getAccessTokenSilently();

      // Retrieve tokens from localStorage
      const tokensString = localStorage.getItem("jobClaimTokens");
      const storedTokens = tokensString
        ? (JSON.parse(tokensString) as string[])
        : [];

      if (!Array.isArray(storedTokens) || storedTokens.length === 0) {
        return { claimed: [], errors: [] };
      }

      return await request<ClaimOpportunitiesResponse>({
        method: "POST",
        path: `/api/jobOpening/claim`,
        token,
        body: JSON.stringify({
          tokens: storedTokens,
        }),
      });
    },
    onSuccess: async (data) => {
      if (!data.claimed.length && !data.errors.length) {
        return;
      }

      localStorage.removeItem("jobClaimTokens");
      await invalidate(QueryKeys.companyOpportunities);

      navigate("/opportunities");
    },
  });

  return {
    claimOpportunities,
    claimOpportunitiesPending,
    claimOpportunitiesError,
  };
};
